import React from 'react'
import styled from 'styled-components'

import { palette, fonts, mediaQueries } from '../../styles/variables'
import { translate } from '../../utils/translation'
import { DefaultButton, PrimaryButton } from '../buttons/Buttons'
import { sendMessage } from '../../utils/functions'
import {
  KISSMETRICS_TRACK_EVENTS,
  kissmetricsRecordingEvent,
  PROPERTY_NAMES,
  PROPERTY_VALUES,
} from '../../utils/kissmetrics'
import {
  getUserTier,
  isFreeTrialUsed,
  isPaidUser,
} from '../../utils/freeVersionBlock'
import { Text } from '../topography'
import {
  getUserFreeTrialDays,
  isSignedIn,
  isSubUser,
  shouldUpdateBillingInfo,
} from '../../utils/userAuth'
import { Mixpanel } from '../../utils/mixpanel'

import Modal, { ModalHeader, ModalContent, ModalFooter } from './Modal'

const Title = styled.span({
  color: palette.darkGray,
  fontSize: 24,
  fontFamily: fonts.primaryBook,
  textAlign: 'center',
  marginLeft: '8px',
  [mediaQueries.mobile]: {
    fontSize: '18px',
    marginLeft: 0,
    marginTop: 20,
    transform: 'translateX(0)',
  },
})

const Row = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  [mediaQueries.mobile]: {
    flexDirection: 'column',
  },
})

const desktopStyle = {}

const mobileStyle = {
  width: '90%',
  height: 'auto',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-around',
}

const PModalFooter = styled(ModalFooter)({})

const FooterSection = styled.div({
  display: 'flex',
  justifyContent: 'center',
  width: '100%',

  '& button': {
    [mediaQueries.mobile]: {
      marginTop: '15px',
    },
  },

  '& button + button': {
    [mediaQueries.mobile]: {
      marginTop: '0px',
    },
  },

  [mediaQueries.mobile]: {
    flexDirection: 'column-reverse',
    justifyContent: 'center',
  },
})

export default ({
  title,
  message,
  onUpgrade,
  onDeny,
  onClose,
  isOpen,
  forceUpgrade,
  eventProperty = {},
  showLoginLink,
  addonType,
  addonProjectId,
  onClickBuyAddon,
  from = '',
  ...rest
}) => {
  const isSignedInUser = isSignedIn()
  const isPaidUserAccount = isPaidUser()
  const isSubUserAccount = isSubUser()
  const { eventProps } = eventProperty

  const getCopyText = () => {
    if (isPaidUserAccount || isFreeTrialUsed()) {
      return translate('upgrade_cta_button')
    }
    if (
      [
        'credits',
        'backlink_opportunity',
        'backlinks',
        'top_pages_by_traffic',
        'content_ideas',
        'similar_websites',
        'keywords_by_traffic',
        'keyword_ideas',
        'site_audit',
      ].includes(from)
    ) {
      return translate('start_trial_button')
    }
    return translate('seven_day_free', getUserFreeTrialDays())
  }

  return (
    <Modal
      isOpen={isOpen}
      desktopStyle={desktopStyle}
      mobileStyle={mobileStyle}
      onClose={onClose}
      {...rest}
    >
      <ModalHeader data-testid="prompt-modal-header">
        <Row>
          <Title dangerouslySetInnerHTML={{ __html: title }} />
        </Row>
      </ModalHeader>
      <ModalContent
        style={{ height: 'calc(100% - 200px)' }}
        data-testid="prompt-modal-content"
      >
        <Text
          color={palette.darkGray}
          fontFamily={fonts.primaryLight}
          textAlign="center"
          lineHeight="25px"
          whiteSpace="pre-wrap"
          mobilePadding="0 20px"
          dangerouslySetInnerHTML={{ __html: message }}
        />
      </ModalContent>
      {isSignedInUser || forceUpgrade ? (
        <PModalFooter>
          <FooterSection>
            <DefaultButton
              static
              color={palette.darkGray50}
              onClick={onDeny}
              style={{ flex: 1 }}
              data-testid="prompt-modal-cancel-button"
            >
              {isSubUserAccount ? translate('close') : translate('no_thanks')}
            </DefaultButton>
            {addonType && (
              <DefaultButton
                static
                color={palette.darkGray50}
                onClick={() => {
                  if (shouldUpdateBillingInfo()) {
                    sendMessage('SHOW_UPDATE_BILLING_INFO_MODAL')
                    onClose()
                    return
                  }
                  onClickBuyAddon(addonType, addonProjectId)
                }}
                style={{
                  flex: 1,
                  backgroundColor: isSubUserAccount
                    ? palette.lightGray
                    : palette.blue,
                  borderColor: isSubUserAccount
                    ? palette.lightGray
                    : palette.blue,
                  color: palette.white,
                }}
                disabled={isSubUserAccount}
              >
                {translate('buy_addon_button')}
              </DefaultButton>
            )}
            {!addonType && getUserTier() !== 'tier3' && (
              <DefaultButton
                static
                onClick={() => {
                  if (isPaidUser() && shouldUpdateBillingInfo()) {
                    sendMessage('SHOW_UPDATE_BILLING_INFO_MODAL')
                    return
                  }
                  if (eventProps) {
                    kissmetricsRecordingEvent(
                      !isFreeTrialUsed() && from === 'credits'
                        ? KISSMETRICS_TRACK_EVENTS.clicked_start_my_free_trial
                        : KISSMETRICS_TRACK_EVENTS.clicked_upgrade,
                      eventProps,
                    )
                  }
                  onUpgrade()
                }}
                style={{
                  flex: 1,
                  backgroundColor: isSubUserAccount
                    ? palette.lightGray
                    : palette.transparent,
                  borderColor: isSubUserAccount
                    ? palette.lightGray
                    : palette.orange,
                  color: isSubUserAccount ? palette.white : palette.orange,
                }}
                disabled={isSubUserAccount}
              >
                {getCopyText()}
              </DefaultButton>
            )}
          </FooterSection>
          {showLoginLink && !isSignedInUser && (
            <Text
              fontSize="14px"
              lineHeight="18px"
              fontFamily={fonts.primaryBook}
              margin="16px 0 0 0"
              textAlign="center"
            >
              {translate('already_have_account')}
              <Text
                onClick={() => {
                  kissmetricsRecordingEvent(
                    KISSMETRICS_TRACK_EVENTS.clicked_signin,
                    {
                      [PROPERTY_NAMES.signin_location]:
                        PROPERTY_VALUES.signin_location.prompt_modal,
                    },
                  )
                  sendMessage('SHOW_AUTH')
                  onClose()
                }}
                style={{
                  marginLeft: '5px',
                  fontSize: '14px',
                  lineHeight: '18px',
                  color: palette.orange,
                  textDecoration: 'underline',
                  display: 'inline-block',
                  cursor: 'pointer',
                }}
              >
                {translate('sign_in_here')}
              </Text>
            </Text>
          )}
        </PModalFooter>
      ) : (
        <PModalFooter>
          <FooterSection data-testid="prompt-modal-footer">
            <DefaultButton
              data-testid="prompt-modal-cancel-button"
              static
              color={palette.darkGray50}
              onClick={onDeny}
              style={{ flex: 1 }}
            >
              {translate('no_thanks')}
            </DefaultButton>
            <PrimaryButton
              testId="prompt-modal-register-button"
              theme="orange"
              tag={translate('register_for_free')}
              onClick={() => {
                if (eventProperty) {
                  kissmetricsRecordingEvent(
                    KISSMETRICS_TRACK_EVENTS.clicked_register,
                    eventProperty,
                  )
                }
                Mixpanel.saveRegisterClickPageName()
                sendMessage('SHOW_AUTH', { type: 'register' })
                onClose()
              }}
              style={{ flex: 1 }}
              width="100%"
            />
          </FooterSection>
          {showLoginLink && !isSignedInUser && (
            <Text
              fontSize="14px"
              lineHeight="18px"
              fontFamily={fonts.primaryBook}
              margin="16px 0 0 0"
              textAlign="center"
            >
              {translate('already_have_account')}
              <Text
                onClick={() => {
                  kissmetricsRecordingEvent(
                    KISSMETRICS_TRACK_EVENTS.clicked_signin,
                    {
                      [PROPERTY_NAMES.signin_location]:
                        PROPERTY_VALUES.signin_location.prompt_modal,
                    },
                  )
                  sendMessage('SHOW_AUTH')
                  onClose()
                }}
                style={{
                  marginLeft: '5px',
                  fontSize: '14px',
                  lineHeight: '18px',
                  color: palette.orange,
                  textDecoration: 'underline',
                  display: 'inline-block',
                  cursor: 'pointer',
                }}
              >
                {translate('sign_in_here')}
              </Text>
            </Text>
          )}
        </PModalFooter>
      )}
    </Modal>
  )
}
